<template>
  <el-dialog title="商品选择器" :visible.sync="visible" width="805px" append-to-body :close-on-click-modal="false" @close="close">
    <div class="filters">
      <el-select v-model="groupId" placeholder="搜索或选择商品分组" filterable clearable :disabled="busy" @change="search">
        <el-option v-for="group in groups" :key="group.id" :value="group.id" :label="group.name" />
      </el-select>
      <el-input v-model.trim="keyword" placeholder="请输入货号或商品名称" clearable :disabled="busy" @keyup.enter.native="search" />
      <el-button :disabled="busy" @click="search">搜索</el-button>
    </div>
    <div class="picker-body">
      <div v-loading="loading" class="candidates">
        <div v-for="goods in rows" :key="goods.goods_id" class="goods-card" :class="{ selected: isSelected(goods) }" @click="toggle(goods)">
          <span v-if="isSelected(goods)" class="selected-check"><i class="el-icon-check" /></span>
          <img :src="goods.thumbnail" />
          <div><div class="name">{{ goods.goods_name }}</div><div class="sn">{{ goods.sn }}</div><div class="price">¥ {{ goods.price | unitPrice }}</div></div>
        </div>
        <div v-if="!loading && !rows.length" class="empty">暂无商品</div>
      </div>
      <div v-loading="restoring" class="selection">
        <div v-for="goods in selectedRows" :key="goods.goods_id" class="goods-card selected" @click="toggle(goods)">
          <span class="selected-check"><i class="el-icon-check" /></span>
          <img :src="goods.thumbnail" />
          <div><div class="name">{{ goods.goods_name }}</div><div class="sn">{{ goods.sn }}</div><div class="price">¥ {{ goods.price | unitPrice }}</div></div>
        </div>
        <el-pagination v-if="selected.length > 24" small :current-page="selectedPage" :page-size="24" :total="selected.length" layout="prev, pager, next" @current-change="selectedPage = $event" />
      </div>
    </div>
    <el-pagination :disabled="busy || loading" :current-page="pageNo" :page-size="pageSize" :total="total" layout="total, prev, pager, next" @current-change="changePage" />
    <div class="selection-actions">
      <el-checkbox :value="allPagesSelected" :disabled="loading || busy || !total" @change="selectAllPages">全部页全选</el-checkbox>
      <el-button size="small" :disabled="loading || busy || !rows.length" @click="selectCurrentPage">当前页全选</el-button>
      <span>{{ busy ? '正在选择所有匹配商品…' : `按当前搜索条件，共 ${total} 个商品` }}</span>
    </div>
    <div slot="footer" class="footer">
      <span>已选 {{ selected.length }} 个商品</span>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :disabled="busy || restoring" @click="confirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import request from '@/utils/request'
import { api } from '~/ui-domain'
import { decorType } from '@/utils/decor-types'

export default {
  name: 'DecorGoodsPicker',
  props: {
    show: { type: Boolean, default: false },
    defaultData: { type: Array, default: () => [] },
    goodsApi: { type: String, required: true },
    multipleApi: { type: String, required: true }
  },
  data() {
    return {
      visible: false, loading: false, restoring: false, busy: false,
      rows: [], selected: [], groups: [], groupId: '', keyword: '',
      pageNo: 1, pageSize: 12, total: 0, selectedPage: 1,
      appliedKeyword: '', appliedGroupId: '', requestId: 0, sessionId: 0, bulkId: 0,
      allPagesSelected: false, allAddedIds: []
    }
  },
  computed: {
    selectedIds() { return new Set(this.selected.map(item => String(item.goods_id))) },
    selectedRows() { return this.selected.slice((this.selectedPage - 1) * 24, this.selectedPage * 24) }
  },
  watch: {
    show(value) {
      if (value) this.open()
      else { this.visible = false; this.invalidate() }
    }
  },
  methods: {
    normalize(item) {
      return { ...item, goods_id: String(item.goods_id), thumbnail: item.thumbnail || item.goods_image,
        price: item.price == null ? item.goods_price : item.price }
    },
    invalidate() { this.sessionId++; this.requestId++; this.bulkId++; this.busy = false; this.loading = false; this.restoring = false },
    close() { this.invalidate(); this.$emit('close') },
    async open() {
      this.invalidate()
      const session = this.sessionId
      this.visible = true
      this.selectedPage = 1
      this.selected = Array.from(new Map(this.defaultData.filter(item => item && item.goods_id)
        .map(item => [String(item.goods_id), this.normalize(item)])).values())
      this.search()
      const groupApi = decorType === 'seller' ? `${api.seller}/seller/goods-groups` : `${api.admin}/admin/goods/groups`
      request({ url: groupApi, method: 'get', loading: false }).then(response => {
        if (session !== this.sessionId) return
        const options = []
        const append = (items, prefix = '') => (items || []).forEach(item => {
          const name = prefix + item.group_name
          options.push({ id: String(item.group_id), name })
          append(item.children, name + ' / ')
        })
        append(response)
        this.groups = options
      }).catch(() => { if (session === this.sessionId) this.groups = [] })
      // 分批回源已有选品，既保留装修顺序和展示字段，也不把大量ID拼入一个URL。
      if (!this.selected.length) return
      this.restoring = true
      try {
        const ids = this.selected.map(item => item.goods_id)
        const latest = new Map()
        for (let offset = 0; offset < ids.length; offset += 100) {
          const response = await request({ url: this.multipleApi.replace('@ids', ids.slice(offset, offset + 100).join(',')), method: 'get', loading: false })
          if (session !== this.sessionId) return
          response.forEach(item => latest.set(String(item.goods_id), item))
        }
        this.selected = this.selected.map(item => latest.has(item.goods_id) ? this.normalize({ ...item, ...latest.get(item.goods_id) }) : item)
      } catch (error) {
        if (session === this.sessionId) this.$message.error('已选商品资料刷新失败，请重新打开后重试')
      } finally { if (session === this.sessionId) this.restoring = false }
    },
    search() {
      this.bulkId++; this.busy = false; this.allPagesSelected = false; this.allAddedIds = []
      this.appliedKeyword = this.keyword; this.appliedGroupId = this.groupId; this.pageNo = 1
      this.load()
    },
    params() {
      const params = { keyword: this.appliedKeyword }
      if (this.appliedGroupId) params.goods_group_id = this.appliedGroupId
      return params
    },
    changePage(page) { this.pageNo = page; this.load() },
    async load() {
      const id = ++this.requestId
      this.loading = true
      try {
        const response = await request({ url: this.goodsApi, method: 'get', loading: false,
          params: { ...this.params(), page_no: this.pageNo, page_size: this.pageSize } })
        if (id !== this.requestId || !this.visible) return
        this.rows = (response.data || []).map(this.normalize)
        this.total = Number(response.data_total || 0)
      } catch (error) {
        // 保留分页总数，避免失败后自动回跳第一页。
        if (id === this.requestId) { this.rows = [] }
      } finally { if (id === this.requestId) this.loading = false }
    },
    mergeSelection(goods) {
      const merged = new Map(this.selected.map(item => [String(item.goods_id), item]))
      goods.forEach(item => { if (!merged.has(String(item.goods_id))) merged.set(String(item.goods_id), this.normalize(item)) })
      this.selected = Array.from(merged.values())
    },
    selectCurrentPage() { this.mergeSelection(this.rows) },
    async selectAllPages(checked) {
      if (!checked) {
        const added = new Set(this.allAddedIds)
        this.selected = this.selected.filter(item => !added.has(String(item.goods_id)))
        this.allPagesSelected = false; this.allAddedIds = []; this.selectedPage = 1
        return
      }
      const id = ++this.bulkId
      const existing = new Set(this.selectedIds)
      this.busy = true
      try {
        const response = await request({ url: this.goodsApi + '/all', method: 'get', loading: false, timeout: 120000, params: this.params() })
        if (id !== this.bulkId || !this.visible) return
        this.allAddedIds = response.map(item => String(item.goods_id)).filter(key => !existing.has(key))
        this.mergeSelection(response)
        this.allPagesSelected = response.length > 0
      } catch (error) {
        if (id === this.bulkId) this.allPagesSelected = false
      } finally { if (id === this.bulkId) this.busy = false }
    },
    isSelected(goods) { return this.selectedIds.has(String(goods.goods_id)) },
    toggle(goods) {
      if (this.busy || this.restoring) return
      const index = this.selected.findIndex(item => String(item.goods_id) === String(goods.goods_id))
      if (index < 0) this.mergeSelection([goods])
      else {
        this.selected.splice(index, 1); this.allPagesSelected = false
        this.selectedPage = Math.max(1, Math.min(this.selectedPage, Math.ceil(this.selected.length / 24)))
      }
    },
    confirm() {
      this.$emit('confirm', this.selected.map(item => ({ ...item, goods_image: item.thumbnail, goods_price: item.price })))
      this.visible = false
    }
  }
}
</script>

<style scoped>
.filters { display: flex; gap: 10px; margin-bottom: 16px; }
.filters .el-select { width: 180px; flex-shrink: 0; }
.filters .el-input { width: 240px; flex-shrink: 0; }
.picker-body { display: flex; gap: 16px; height: 340px; margin-bottom: 16px; }
.candidates, .selection { width: 50%; overflow-y: auto; background: #f7f8fa; padding: 6px; box-sizing: border-box; display: flex; flex-wrap: wrap; align-content: flex-start; }
.goods-card { position: relative; width: calc(50% - 6px); min-height: 128px; margin: 3px; padding: 10px 8px; box-sizing: border-box; display: flex; cursor: pointer; background: white; border: 1px solid transparent; border-radius: 4px; }
.goods-card:hover { border-color: #fbc4c4; }
.goods-card.selected { border-color: #f56c6c; box-shadow: 0 0 0 1px rgba(245,108,108,.1); }
.selected-check { position: absolute; top: 0; right: 0; width: 0; height: 0; border-top: 24px solid #f56c6c; border-left: 24px solid transparent; }
.selected-check i { position: absolute; right: 1px; top: -23px; font-size: 12px; font-weight: bold; color: #fff; }
.selection .el-pagination { width: 100%; margin-top: 8px; }
.goods-card img { width: 52px; height: 70px; object-fit: contain; margin-right: 8px; }
.goods-card > div { min-width: 0; }
.name { font-size: 12px; line-height: 17px; word-break: break-word; }
.sn { color: #999; font-size: 12px; word-break: break-all; margin: 4px 0; }
.price { color: #f56c6c; font-size: 12px; }
.empty { width: 100%; text-align: center; padding-top: 60px; color: #999; }
.footer > span { float: left; line-height: 36px; }
.selection-actions { display: flex; align-items: center; gap: 14px; margin-top: 14px; }
.selection-actions > span { color: #999; font-size: 12px; }
</style>
